import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v2.94 · Short-Circuit graphical editor. Nodes:
   - 1 UTILITY  (voltage_kv, source_sc_mva, x_over_r)
   - 0/1 TRANSFORMER (transformer_kva, transformer_impedance_pct)
   - 1 FAULT_BUS (fault_type)
   - N MOTOR/GENERATOR (tag, rated_kva, x_subtransient_pu, kind, x_over_r)
   Edges are visual only — they don't carry SC data. */

const Utility     = (p) => <BusNode {...p} {...NODE_STYLE.UTILITY}     sub={(d) => `${d.voltage_kv} kV · ${d.source_sc_mva} MVA · X/R ${d.x_over_r}`} />;
const Transformer = (p) => <BusNode {...p} {...NODE_STYLE.TRANSFORMER} sub={(d) => d.transformer_kva ? `${d.transformer_kva} kVA · Z=${d.transformer_impedance_pct}%` : "no transformer"} />;
const FaultBus    = (p) => <BusNode {...p} {...NODE_STYLE.FAULT_BUS}   sub={(d) => `fault: ${d.fault_type}`} />;
const Motor       = (p) => <BusNode {...p} {...NODE_STYLE.MOTOR}       sub={(d) => `${d.rated_kva} kVA · X"d=${d.x_subtransient_pu} · ${d.kind}`} />;
const Generator   = (p) => <BusNode {...p} {...NODE_STYLE.GENERATOR}   sub={(d) => `${d.rated_kva} kVA · X"d=${d.x_subtransient_pu} · gen`} />;
const nodeTypes = { UTILITY: Utility, TRANSFORMER: Transformer, FAULT_BUS: FaultBus, MOTOR: Motor, GENERATOR: Generator };

const INITIAL_NODES = [
  { id: "U1", type: "UTILITY", position: { x: 40, y: 160 },
    data: { id: "U1", voltage_kv: 13.8, source_sc_mva: 250, x_over_r: 10 } },
  { id: "T1", type: "TRANSFORMER", position: { x: 250, y: 160 },
    data: { id: "T1", transformer_kva: 2000, transformer_impedance_pct: 5.75 } },
  { id: "F1", type: "FAULT_BUS", position: { x: 460, y: 160 },
    data: { id: "F1", fault_type: "3P" } },
  { id: "M1", type: "MOTOR", position: { x: 460, y: 40 },
    data: { id: "M1", tag: "M1", rated_kva: 1500, x_subtransient_pu: 0.17, kind: "induction", x_over_r: 6 } },
];
const INITIAL_EDGES = [
  { id: "e1", source: "U1", target: "T1", ...edgeStyle() },
  { id: "e2", source: "T1", target: "F1", ...edgeStyle() },
  { id: "e3", source: "M1", target: "F1", ...edgeStyle() },
];

const FIELDS = {
  UTILITY: [
    { key: "id",              label: "Tag",                  type: "text" },
    { key: "voltage_kv",      label: "Voltage (kV L-L)",     type: "number", step: "0.1" },
    { key: "source_sc_mva",   label: "SC MVA",               type: "number" },
    { key: "x_over_r",        label: "X / R",                type: "number", step: "0.1" },
  ],
  TRANSFORMER: [
    { key: "id",                             label: "Tag",             type: "text" },
    { key: "transformer_kva",                label: "Rated kVA (0 = skip)", type: "number" },
    { key: "transformer_impedance_pct",      label: "Impedance %",     type: "number", step: "0.01" },
  ],
  FAULT_BUS: [
    { key: "id",         label: "Tag",        type: "text" },
    { key: "fault_type", label: "Fault type", type: "select",
      options: [{ value: "3P", label: "3-phase" }, { value: "LG", label: "L-G" }, { value: "LL", label: "L-L" }] },
  ],
  MOTOR: [
    { key: "tag",                label: "Tag",           type: "text" },
    { key: "rated_kva",          label: "Rated kVA",     type: "number" },
    { key: "x_subtransient_pu",  label: 'X"d (pu)',      type: "number", step: "0.01" },
    { key: "kind",               label: "Kind",          type: "select",
      options: [{ value: "induction", label: "induction motor" },
                { value: "synchronous", label: "synchronous motor" },
                { value: "generator", label: "generator" }] },
    { key: "x_over_r",           label: "X / R",         type: "number", step: "0.5" },
  ],
  GENERATOR: [
    { key: "tag",                label: "Tag",           type: "text" },
    { key: "rated_kva",          label: "Rated kVA",     type: "number" },
    { key: "x_subtransient_pu",  label: 'X"d (pu)',      type: "number", step: "0.01" },
    { key: "x_over_r",           label: "X / R",         type: "number", step: "0.5" },
  ],
};

/* Serialise the graph into the /api/studies/short-circuit payload. */
export function graphToShortCircuit(nodes) {
  const utility = nodes.find((n) => n.type === "UTILITY")?.data || {};
  const tx      = nodes.find((n) => n.type === "TRANSFORMER")?.data || {};
  const fault   = nodes.find((n) => n.type === "FAULT_BUS")?.data || {};
  const machines = nodes.filter((n) => n.type === "MOTOR" || n.type === "GENERATOR").map((n) => ({
    tag:                n.data.tag || n.data.id,
    rated_kva:          Number(n.data.rated_kva || 0),
    x_subtransient_pu:  Number(n.data.x_subtransient_pu || 0.17),
    kind:               n.type === "GENERATOR" ? "generator" : (n.data.kind || "induction"),
    x_over_r:           Number(n.data.x_over_r || 6),
  }));
  return {
    voltage_kv:                 Number(utility.voltage_kv || 13.8),
    source_sc_mva:              Number(utility.source_sc_mva || 250),
    x_over_r:                   Number(utility.x_over_r || 10),
    transformer_kva:            Number(tx.transformer_kva || 0),
    transformer_impedance_pct:  Number(tx.transformer_impedance_pct || 5.75),
    fault_type:                 fault.fault_type || "3P",
    motors:                     machines,
  };
}

function ShortCircuitSLDInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToShortCircuit(nodes)); }, [nodes, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, id: `e${idCounter.current++}`, ...edgeStyle() }, es));
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type[0]}${idCounter.current++}`;
    const defaults = type === "MOTOR"
      ? { tag: id, rated_kva: 500, x_subtransient_pu: 0.17, kind: "induction", x_over_r: 6 }
      : type === "GENERATOR"
      ? { tag: id, rated_kva: 1000, x_subtransient_pu: 0.15, kind: "generator", x_over_r: 30 }
      : type === "TRANSFORMER"
      ? { transformer_kva: 1500, transformer_impedance_pct: 5.75 }
      : type === "FAULT_BUS"
      ? { fault_type: "3P" }
      : { voltage_kv: 13.8, source_sc_mva: 250, x_over_r: 10 };
    setNodes((ns) => [...ns, {
      id, type,
      position: { x: 120 + Math.random() * 400, y: 40 + Math.random() * 280 },
      data: { id, ...defaults },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };
  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="sc-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#FFCC00" onClick={() => addNode("UTILITY")} testid="sc-add-utility" title="Add utility source">
          <Plus size={10} className="inline" /> utility
        </ToolbarBtn>
        <ToolbarBtn colour="#B0A0FF" onClick={() => addNode("TRANSFORMER")} testid="sc-add-tx" title="Add transformer">
          <Plus size={10} className="inline" /> tx
        </ToolbarBtn>
        <ToolbarBtn colour="#FF3B30" onClick={() => addNode("FAULT_BUS")} testid="sc-add-fault" title="Add fault bus">
          <Plus size={10} className="inline" /> fault
        </ToolbarBtn>
        <ToolbarBtn colour="#00E5FF" onClick={() => addNode("MOTOR")} testid="sc-add-motor" title="Add motor">
          <Plus size={10} className="inline" /> motor
        </ToolbarBtn>
        <ToolbarBtn colour="#7CFF6B" onClick={() => addNode("GENERATOR")} testid="sc-add-gen" title="Add generator">
          <Plus size={10} className="inline" /> gen
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel} testid="sc-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll} testid="sc-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.tag || sel.node.data.id}
          subtitle={`${sel.node.type.replace("_", " ").toLowerCase()}`}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function ShortCircuitSLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <ShortCircuitSLDInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
